import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { ArrowUp, Sparkles, X, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo } from "./Logo";
import type { AssistantMessage } from "../types";

const SUGGESTIONS = [
  "Hur mycket har vi spenderat den här månaden?",
  "Vilka fasta kostnader borde vi se över?",
  "Hur ser vår nettoförmögenhet ut?",
  "Hur går våra investeringar?",
];

/** Minimal markdown: **bold**, bullet lists, paragraphs. Enough for chat answers. */
function renderInline(text: string, keyPrefix: string): ReactNode[] {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) =>
    part.startsWith("**") && part.endsWith("**") ? (
      <strong key={`${keyPrefix}-${i}`}>{part.slice(2, -2)}</strong>
    ) : (
      <span key={`${keyPrefix}-${i}`}>{part}</span>
    )
  );
}

function renderMarkdown(content: string): ReactNode {
  const blocks: ReactNode[] = [];
  const lines = content.split("\n");
  let bullets: string[] = [];

  const flushBullets = (key: string) => {
    if (bullets.length === 0) return;
    blocks.push(
      <ul key={key} className="my-1 space-y-1 pl-4">
        {bullets.map((b, i) => (
          <li key={i} className="list-disc">
            {renderInline(b, `${key}-${i}`)}
          </li>
        ))}
      </ul>
    );
    bullets = [];
  };

  lines.forEach((line, i) => {
    const bulletMatch = line.match(/^\s*[-•*]\s+(.*)$/);
    if (bulletMatch) {
      bullets.push(bulletMatch[1] ?? "");
      return;
    }
    flushBullets(`ul-${i}`);
    const trimmed = line.replace(/^#{1,4}\s*/, "");
    if (trimmed.trim().length > 0) {
      blocks.push(
        <p key={`p-${i}`} className="my-1">
          {renderInline(trimmed, `p-${i}`)}
        </p>
      );
    }
  });
  flushBullets("ul-end");

  return <>{blocks}</>;
}

/** Parse OpenRouter SSE chunks and append delta text via callback */
async function readStream(
  body: ReadableStream<Uint8Array>,
  onDelta: (text: string) => void
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    const lines = buffer.split("\n");
    buffer = lines.pop() ?? "";

    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed.startsWith("data:")) continue;
      const payload = trimmed.slice(5).trim();
      if (payload === "[DONE]") return;
      try {
        const parsed = JSON.parse(payload);
        const delta = parsed.choices?.[0]?.delta?.content;
        if (typeof delta === "string" && delta.length > 0) {
          onDelta(delta);
        }
      } catch {
        // Partial JSON in a split chunk — ignored, the buffer catches it next round
      }
    }
  }
}

export function AssistantChat() {
  const [open, setOpen] = useState(false);
  const [available, setAvailable] = useState<boolean | null>(null);
  const [messages, setMessages] = useState<AssistantMessage[]>([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetch("/api/assistant")
      .then((r) => r.json())
      .then((d) => setAvailable(!!d.available))
      .catch(() => setAvailable(false));
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, streaming]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  async function ask(question: string) {
    if (streaming || !question.trim()) return;
    setError(null);

    const history: AssistantMessage[] = [
      ...messages,
      { role: "user", content: question.trim() },
    ];
    setMessages([...history, { role: "assistant", content: "" }]);
    setInput("");
    setStreaming(true);

    try {
      const response = await fetch("/api/assistant", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ messages: history }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.error ?? "AI-assistenten kunde inte svara.");
      }
      if (!response.body) throw new Error("Tomt svar från servern.");

      await readStream(response.body, (delta) => {
        setMessages((prev) => {
          const next = [...prev];
          const last = next[next.length - 1];
          if (last?.role === "assistant") {
            next[next.length - 1] = { ...last, content: last.content + delta };
          }
          return next;
        });
      });
    } catch (err: any) {
      setError(err.message ?? "Något gick fel.");
      // Drop the empty assistant bubble if nothing arrived
      setMessages((prev) =>
        prev.length > 0 && prev[prev.length - 1]?.role === "assistant" && !prev[prev.length - 1]?.content
          ? prev.slice(0, -1)
          : prev
      );
    } finally {
      setStreaming(false);
    }
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    void ask(input);
  }

  return (
    <>
      {/* Floating button — above the mobile bottom nav, corner on desktop */}
      <button
        onClick={() => setOpen(true)}
        aria-label="Öppna AI-assistenten"
        className={`fixed bottom-20 right-4 z-40 flex h-13 w-13 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-transform hover:scale-105 active:scale-95 lg:bottom-6 lg:right-6 ${open ? "hidden" : ""}`}
        style={{ height: 52, width: 52 }}
      >
        <Sparkles className="h-5 w-5" />
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex flex-col bg-card lg:inset-auto lg:bottom-6 lg:right-6 lg:h-[620px] lg:max-h-[85vh] lg:w-[420px] lg:rounded-2xl lg:border lg:shadow-2xl">
          <div className="flex items-center gap-2 border-b px-4 py-3">
            <Logo className="h-5 w-5 text-primary" />
            <div>
              <h2 className="text-sm font-semibold leading-tight">AI-assistent</h2>
              <p className="text-xs text-muted-foreground leading-tight">
                Frågor om hela familjens ekonomi
              </p>
            </div>
            <div className="ml-auto flex items-center gap-1">
              {messages.length > 0 && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  title="Ny konversation"
                  onClick={() => {
                    setMessages([]);
                    setError(null);
                  }}
                >
                  <RotateCcw className="h-4 w-4" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                aria-label="Stäng"
                onClick={() => setOpen(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
            {available === false && (
              <div className="rounded-lg bg-warning/10 px-3 py-2 text-sm text-warning">
                AI-assistenten kräver att OPENROUTER_API_KEY är satt i servermiljön.
              </div>
            )}

            {messages.length === 0 && available !== false && (
              <div className="space-y-3">
                <div className="rounded-2xl rounded-tl-sm bg-muted px-3.5 py-2.5 text-sm">
                  Hej! Jag kan svara på frågor om er samlade ekonomi — utgifter,
                  budget, fasta kostnader, investeringar, krypto, konton och
                  nettoförmögenhet. Vad undrar du?
                </div>
                <div className="flex flex-wrap gap-2">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => void ask(s)}
                      className="rounded-full border px-3 py-1.5 text-left text-xs text-muted-foreground transition-colors hover:border-primary/50 hover:text-foreground"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((message, i) => (
              <div
                key={i}
                className={message.role === "user" ? "flex justify-end" : "flex justify-start"}
              >
                <div
                  className={
                    message.role === "user"
                      ? "max-w-[85%] rounded-2xl rounded-br-sm bg-primary px-3.5 py-2 text-sm text-primary-foreground"
                      : "max-w-[92%] rounded-2xl rounded-tl-sm bg-muted px-3.5 py-2 text-sm"
                  }
                >
                  {message.role === "assistant" ? (
                    message.content ? (
                      renderMarkdown(message.content)
                    ) : (
                      <span className="inline-flex gap-1 py-1" aria-label="Skriver...">
                        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground [animation-delay:0ms]" />
                        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground [animation-delay:150ms]" />
                        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground [animation-delay:300ms]" />
                      </span>
                    )
                  ) : (
                    message.content
                  )}
                </div>
              </div>
            ))}

            {error && (
              <div className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error}
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit} className="flex items-center gap-2 border-t p-3">
            <input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Fråga om er ekonomi..."
              disabled={available === false}
              className="h-10 flex-1 rounded-full border bg-background px-4 text-sm outline-none focus:border-ring"
            />
            <Button
              type="submit"
              size="icon"
              className="h-10 w-10 shrink-0 rounded-full"
              disabled={!input.trim() || streaming || available === false}
              aria-label="Skicka"
            >
              <ArrowUp className="h-4 w-4" />
            </Button>
          </form>
        </div>
      )}
    </>
  );
}
